import React, { useEffect, useRef } from "react";
import {
  FaUserMd,
  FaMicroscope,
  FaPills,
  FaUsers,
  FaHeart,
  FaShieldAlt,
  FaMobileAlt,
  FaCoins,
  FaHandshake,
  FaGlobe,
  FaStethoscope,
  FaHeartbeat,
  FaCheckCircle,
  FaRocket,
  FaEye,
  FaHandsHelping,
  FaChartLine,
  FaClock,
  FaMapMarkerAlt,
} from "react-icons/fa";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import DownloadModal from "../../components/DownloadModal/DownloadModal";
import styles from "./About.module.css";
import { ROUTES } from "../../constants/routes";

const AboutPage: React.FC = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Initialize scroll reveal animations
    const observerOptions = {
      threshold: 0.2,
      rootMargin: "0px 0px -20px 0px",
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");

          // Trigger counter animation for stat items
          if (entry.target.classList.contains(styles.statItem)) {
            setTimeout(() => {
              const numberElement = entry.target.querySelector(
                `.${styles.statNumber}`
              ) as HTMLElement;
              if (numberElement && !numberElement.dataset.animated) {
                animateCounter(numberElement);
              }
            }, 300);
          }
        }
      });
    }, observerOptions);

    // Observe all scroll reveal elements
    const elementsToObserve = document.querySelectorAll(
      ".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale"
    );

    const statItems = document.querySelectorAll(`.${styles.statItem}`);
    const allElements = Array.from(elementsToObserve).concat(
      Array.from(statItems)
    );

    allElements.forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const animateCounter = (element: HTMLElement) => {
    if (element.dataset.animated) return;
    element.dataset.animated = "true";

    const target = parseFloat(element.dataset.count || "0");
    const duration = 2500;
    const start = performance.now();
    const startValue = 0;

    const updateCounter = (currentTime: number) => {
      const elapsed = currentTime - start;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (target - startValue) * easeOutQuart;

      let displayValue: string;

      if (target === 2020) {
        displayValue = "2020";
      } else if (target === 20000) {
        displayValue = Math.floor(currentValue / 1000) + "K+";
      } else if (target === 800) {
        displayValue = Math.floor(currentValue) + "+";
      } else if (target === 36) {
        displayValue = Math.floor(currentValue).toString();
      } else if (target === 50) {
        displayValue = Math.floor(currentValue) + "%";
      } else if (target === 99.8) {
        displayValue = currentValue.toFixed(1) + "%";
      } else {
        displayValue = Math.floor(currentValue).toString();
      }

      element.textContent = displayValue;

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    };

    requestAnimationFrame(updateCounter);
  };

  const ecosystemMembers = [
    {
      icon: <FaUserMd />,
      title: "Healthcare Providers",
      count: "800+ Doctors",
      description:
        "Licensed medical professionals who control consultation sessions, manage referrals, and provide expert care through our mobile platform",
      features: [
        "Session control and management",
        "Smart referral system",
        "Practice management tools",
        "Transparent fee structure",
      ],
      gradient: "blue",
    },
    {
      icon: <FaMicroscope />,
      title: "Laboratory Partners",
      count: "500+ Tests Available",
      description:
        "Certified labs that receive doctor referrals, share medical data seamlessly, and upload results directly to patient records",
      features: [
        "Instant result sharing",
        "Seamless referral integration",
        "Quality assurance protocols",
        "Secure data transmission",
      ],
      gradient: "green",
    },
    {
      icon: <FaPills />,
      title: "Pharmacy Network",
      count: "2,000+ Pharmacies",
      description:
        "Verified pharmacies that fulfill digital prescriptions, ensure medication authenticity, and provide doorstep delivery services",
      features: [
        "Digital prescription processing",
        "Drug verification system",
        "Doorstep delivery",
        "Counterfeit prevention",
      ],
      gradient: "orange",
    },
    {
      icon: <FaUsers />,
      title: "Patients & Community",
      count: "20K+ Active Users",
      description:
        "Patients who earn fee cashback, access quality healthcare, donate to help others, and build a supportive health community",
      features: [
        "Up to 50% fee cashback rewards",
        "Community donations",
        "24/7 healthcare access",
        "Health records management",
      ],
      gradient: "red",
    },
  ];

  const missionValues = [
    {
      icon: <FaEye />,
      title: "Our Vision",
      description:
        "To become Africa's leading healthcare ecosystem where quality medical care is accessible, affordable, and rewarding for everyone",
      highlights: [
        "Pan-African expansion",
        "Universal healthcare access",
        "Technology-driven solutions",
      ],
    },
    {
      icon: <FaRocket />,
      title: "Our Mission",
      description:
        "Revolutionizing healthcare delivery through mobile technology, transparent pricing, and community-driven support systems",
      highlights: [
        "Mobile-first approach",
        "Transparent pricing",
        "Community empowerment",
      ],
    },
    {
      icon: <FaHandsHelping />,
      title: "Our Values",
      description:
        "Compassion, innovation, transparency, and community support guide everything we do in transforming African healthcare",
      highlights: [
        "Patient-centered care",
        "Ethical practices",
        "Continuous innovation",
      ],
    },
  ];

  const impactStats = [
    {
      number: "2020",
      label: "Founded",
      icon: <FaRocket />,
      description: "Started with a vision to transform African healthcare",
    },
    {
      number: "20000",
      label: "Lives Impacted",
      icon: <FaHeart />,
      description: "Patients served across Nigeria and growing",
    },
    {
      number: "800",
      label: "Healthcare Providers",
      icon: <FaUserMd />,
      description: "Licensed doctors, labs, and pharmacies in our network",
    },
    {
      number: "36",
      label: "States Covered",
      icon: <FaMapMarkerAlt />,
      description: "Nationwide coverage across Nigeria",
    },
    {
      number: "50",
      label: "Fee Cashback",
      icon: <FaCoins />,
      description: "Maximum cashback percentage on transaction fees",
    },
    {
      number: "99.8",
      label: "Platform Reliability",
      icon: <FaShieldAlt />,
      description: "Uptime and security for critical healthcare services",
    },
  ];

  const technologyInnovations = [
    {
      icon: <FaMobileAlt />,
      title: "Mobile-First Platform",
      description:
        "All healthcare services happen in our intuitive mobile app with offline capabilities",
    },
    {
      icon: <FaCoins />,
      title: "Fee Cashback System",
      description:
        "Revolutionary reward system that gives patients up to 50% cashback on transaction fees",
    },
    {
      icon: <FaShieldAlt />,
      title: "Virtual Banking",
      description:
        "Secure virtual accounts for seamless transactions and cashback management",
    },
    {
      icon: <FaHandshake />,
      title: "Smart Referrals",
      description:
        "AI-powered referral system connecting patients with the right specialists and labs",
    },
    {
      icon: <FaHeart />,
      title: "Community Support",
      description:
        "Crowdfunding platform for medical expenses with transparent donation tracking",
    },
    {
      icon: <FaChartLine />,
      title: "Analytics Dashboard",
      description:
        "Real-time insights for healthcare providers and administrators",
    },
  ];

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <div className={styles.aboutPage}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}>
                <FaStethoscope className={styles.badgeIcon} />
                About LicoMeds
              </div>

              <h1 className={styles.heroTitle}>
                Revolutionizing Healthcare
                <span className={styles.heroAccent}> Across Africa</span>
              </h1>

              <p className={styles.heroSubtitle}>
                Born in Nigeria, designed for Africa. We're building the
                continent's most comprehensive healthcare ecosystem where
                patients, doctors, labs, and pharmacies connect seamlessly
                through our mobile platform with transparent pricing and
                community-driven support.
              </p>

              <div className={styles.heroFoundingStory}>
                <div className={styles.foundingItem}>
                  <span className={styles.foundingYear}>2020</span>
                  <span className={styles.foundingLabel}>Founded</span>
                </div>
                <div className={styles.foundingSeparator}></div>
                <div className={styles.foundingItem}>
                  <span className={styles.foundingChallenge}>
                    Healthcare Gap
                  </span>
                  <span className={styles.foundingLabel}>Challenge</span>
                </div>
                <div className={styles.foundingSeparator}></div>
                <div className={styles.foundingItem}>
                  <span className={styles.foundingSolution}>
                    Mobile Solution
                  </span>
                  <span className={styles.foundingLabel}>Innovation</span>
                </div>
              </div>

              <div className={styles.heroActions}>
                <DownloadModal variant="button" className={styles.btnPrimary} />
                <a href="#mission" className={styles.btnOutline}>
                  <FaHeartbeat className={styles.btnIcon} />
                  Learn Our Story
                </a>
              </div>
            </div>

            <div className={styles.heroVisual}>
              <div
                className={`${styles.heroImageContainer} ${styles.floatGentle}`}
              >
                <div className={`${styles.heroIcon} ${styles.heartbeatFlow}`}>
                  <FaHeartbeat />
                </div>
                <div className={styles.heroRipples}>
                  <div className={styles.ripple}></div>
                  <div className={styles.ripple}></div>
                  <div className={styles.ripple}></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Statistics */}
        <section className={styles.statsSection}>
          <div className={styles.container}>
            <div className={styles.statsGrid}>
              {impactStats.map((stat, index) => (
                <div
                  key={index}
                  className={`${styles.statItem} scroll-reveal-scale`}
                >
                  <div className={styles.statIcon}>{stat.icon}</div>
                  <div className={styles.statNumber} data-count={stat.number}>
                    0
                  </div>
                  <div className={styles.statLabel}>{stat.label}</div>
                  <div className={styles.statDescription}>
                    {stat.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission, Vision, Values */}
        <section className={styles.missionSection} id="mission">
          <div className={styles.container}>
            <div className={`${styles.sectionHeader} scroll-reveal`}>
              <h2 className={styles.sectionTitle}>
                Our <span className={styles.textGradient}>Purpose</span>
              </h2>
              <p className={styles.sectionSubtitle}>
                Driven by purpose, guided by values, and committed to
                transforming healthcare across Africa through innovation and
                community
              </p>
            </div>

            <div className={styles.missionGrid}>
              {missionValues.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.missionCard} scroll-reveal`}
                >
                  <div className={styles.missionIcon}>{item.icon}</div>
                  <h3 className={styles.missionTitle}>{item.title}</h3>
                  <p className={styles.missionDescription}>
                    {item.description}
                  </p>
                  <ul className={styles.missionHighlights}>
                    {item.highlights.map((highlight, idx) => (
                      <li key={idx}>
                        <FaCheckCircle className={styles.checkIcon} />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ecosystem Members */}
        <section className={styles.ecosystemSection}>
          <div className={styles.container}>
            <div className={`${styles.sectionHeader} scroll-reveal`}>
              <h2 className={styles.sectionTitle}>
                Our{" "}
                <span className={styles.textGradient}>
                  Healthcare Ecosystem
                </span>
              </h2>
              <p className={styles.sectionSubtitle}>
                A comprehensive network of healthcare stakeholders working
                together to provide seamless, accessible, and affordable medical
                care
              </p>
            </div>

            <div className={styles.ecosystemGrid}>
              {ecosystemMembers.map((member, index) => (
                <div
                  key={index}
                  className={`${styles.ecosystemCard} ${
                    styles[`gradient${member.gradient}`]
                  } scroll-reveal`}
                >
                  <div className={styles.ecosystemHeader}>
                    <div className={styles.ecosystemIcon}>{member.icon}</div>
                    <div className={styles.ecosystemInfo}>
                      <h3 className={styles.ecosystemTitle}>{member.title}</h3>
                      <span className={styles.ecosystemCount}>
                        {member.count}
                      </span>
                    </div>
                  </div>

                  <p className={styles.ecosystemDescription}>
                    {member.description}
                  </p>

                  <ul className={styles.ecosystemFeatures}>
                    {member.features.map((feature, idx) => (
                      <li key={idx}>
                        <FaCheckCircle className={styles.featureCheck} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology & Innovation */}
        <section className={styles.technologySection}>
          <div className={styles.container}>
            <div className={styles.technologyContent}>
              <div className={`${styles.technologyText} scroll-reveal-left`}>
                <h2 className={styles.sectionTitle}>
                  Technology &{" "}
                  <span className={styles.textGradient}>Innovation</span>
                </h2>
                <p className={styles.sectionSubtitle}>
                  Cutting-edge technology solutions designed specifically for
                  African healthcare needs, ensuring accessibility, security,
                  and seamless user experience
                </p>
              </div>

              <div className={styles.technologyGrid}>
                {technologyInnovations.map((tech, index) => (
                  <div
                    key={index}
                    className={`${styles.technologyCard} scroll-reveal`}
                  >
                    <div className={styles.technologyIcon}>{tech.icon}</div>
                    <h4 className={styles.technologyTitle}>{tech.title}</h4>
                    <p className={styles.technologyDescription}>
                      {tech.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className={styles.ctaSection}>
          <div className={styles.container}>
            <div className={`${styles.ctaContent} scroll-reveal`}>
              <div className={styles.ctaIcon}>
                <FaGlobe />
              </div>
              <h2 className={styles.ctaTitle}>
                Join the
                <span className={styles.ctaAccent}> Healthcare Revolution</span>
              </h2>
              <p className={styles.ctaSubtitle}>
                Be part of Africa's largest healthcare ecosystem. Whether you're
                a patient seeking quality care, a healthcare provider expanding
                your reach, or someone who believes in accessible healthcare for
                all.
              </p>

              <div className={styles.ctaStats}>
                <div className={styles.ctaStat}>
                  <span className={styles.ctaStatNumber}>24/7</span>
                  <span className={styles.ctaStatLabel}>Medical Support</span>
                </div>
                <div className={styles.ctaStat}>
                  <span className={styles.ctaStatNumber}>50%</span>
                  <span className={styles.ctaStatLabel}>Fee Cashback</span>
                </div>
                <div className={styles.ctaStat}>
                  <span className={styles.ctaStatNumber}>100%</span>
                  <span className={styles.ctaStatLabel}>Secure & Private</span>
                </div>
              </div>

              <div className={styles.ctaActions}>
                <DownloadModal variant="button" className={styles.btnWhite} />
                <a
                  href={ROUTES.EXTERNAL.EMERGENCY_HOTLINE}
                  className={styles.btnOutline}
                >
                  <FaClock />
                  {ROUTES.EXTERNAL.EMERGENCY_DISPLAY}
                </a>
              </div>

              <div className={styles.ctaNote}>
                <FaCheckCircle />
                <small>
                  Free Download • Transparent Fees • Community-Driven • Made in
                  Nigeria
                </small>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default AboutPage;
